import { styled } from "@pigment-css/react";
import { Stop, VoiceRecording } from "@acto/web-common/components";
import { useTranslations } from "@acto/web-common/locales";
import { pressInteraction } from "@acto/taskpane/field-sales-2/styles/pressInteraction";

const Bar = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  width: "100%",
  gap: theme.fieldSalesTwo.spacing[12],
  padding: `${theme.fieldSalesTwo.spacing[8]} ${theme.fieldSalesTwo.spacing[8]} ${theme.fieldSalesTwo.spacing[8]} ${theme.fieldSalesTwo.spacing[20]}`,
  border: `${theme.strokeWidth.thin} solid ${theme.fieldSalesTwo.colors.surface.glassBorderStrong}`,
  borderRadius: theme.fieldSalesTwo.radius.circular,
  background: theme.fieldSalesTwo.colors.surface.glassMedium,
  boxShadow: theme.fieldSalesTwo.shadows.glass,
  backdropFilter: `blur(${theme.fieldSalesTwo.effects.blurSm})`,
  WebkitBackdropFilter: `blur(${theme.fieldSalesTwo.effects.blurSm})`,
  color: theme.fieldSalesTwo.colors.neutral[900],
}));

const Timer = styled("span")(({ theme }) => ({
  flex: 1,
  textAlign: "center",
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[16],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  fontVariantNumeric: "tabular-nums",
  color: theme.fieldSalesTwo.colors.neutral[900],
}));

const StopButton = styled("button")(({ theme }) => ({
  flexShrink: 0,
  width: 40,
  height: 40,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  border: "none",
  borderRadius: theme.fieldSalesTwo.radius.circular,
  background: theme.fieldSalesTwo.colors.neutral[900],
  color: "#fff",
  cursor: "pointer",
  ...pressInteraction(theme, { scale: 0.94 }),
}));

function formatElapsed(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

interface VoiceBarProps {
  elapsedSeconds: number;
  onStop: () => void;
}

/** The recording capsule: a static waveform glyph, the running session timer, and a stop control. */
export function VoiceBar({ elapsedSeconds, onStop }: VoiceBarProps) {
  const t = useTranslations("fieldSalesTwo.recaps");

  return (
    <Bar>
      <VoiceRecording width={16} height={17} />
      <Timer>{formatElapsed(elapsedSeconds)}</Timer>
      <StopButton type="button" aria-label={t("a11y.stopRecording")} onClick={onStop}>
        <Stop />
      </StopButton>
    </Bar>
  );
}
